/**
 * Read/write My Learning filter state in the hash query (e.g. #/tm/coworker/mycourses?mlcat=1).
 * Uses ml* keys to avoid clashing with other query parameters.
 */

export const ML_URL_PARAM_CAT = "mlcat";
export const ML_URL_PARAM_TAGS = "mltags";
export const ML_URL_PARAM_SEARCH = "mlq";
export const ML_URL_PARAM_STATUS = "mlst";
export const ML_URL_PARAM_SORT = "mlsort";

const ML_PARAM_PREFIX = "ml";

/**
 * @returns {{ path: string, params: URLSearchParams }}
 */
export const parseHashPathAndQuery = () => {
  const hash =
    typeof window !== "undefined" ? window.location.hash || "#/" : "#/";
  const qi = hash.indexOf("?");
  if (qi < 0) {
    return { path: hash, params: new URLSearchParams() };
  }
  return {
    path: hash.slice(0, qi),
    params: new URLSearchParams(hash.slice(qi + 1)),
  };
};

/**
 * @param {URLSearchParams} params
 */
export const removeMyLearningFilterParams = (params) => {
  [...params.keys()]
    .filter((k) => k.startsWith(ML_PARAM_PREFIX))
    .forEach((k) => {
      params.delete(k);
    });
};

/**
 * @param {Object} filter
 * @param {number} tagSlotCount
 * @param {{ defaultStatus: string, defaultSortBy: string }} defaults
 */
export const applyMyLearningFiltersToSearchParams = (
  params,
  filter,
  tagSlotCount,
  { defaultStatus, defaultSortBy },
) => {
  removeMyLearningFilterParams(params);

  if (filter.categoryId && Number(filter.categoryId) !== 0) {
    params.set(ML_URL_PARAM_CAT, String(filter.categoryId));
  }

  const tags = Array.isArray(filter.selectedTags) ? filter.selectedTags : [];
  const padded = [...tags];
  while (padded.length < tagSlotCount) {
    padded.push(0);
  }
  const trimmed = padded.slice(0, Math.max(tagSlotCount, tags.length));
  if (trimmed.some((t) => Number(t) !== 0)) {
    params.set(
      ML_URL_PARAM_TAGS,
      trimmed.map((t) => String(Number(t) || 0)).join(","),
    );
  }

  if (filter.searchText && String(filter.searchText).trim()) {
    params.set(ML_URL_PARAM_SEARCH, String(filter.searchText).trim());
  }

  const st = filter.status ? String(filter.status).toLowerCase() : "";
  if (st && st !== String(defaultStatus).toLowerCase()) {
    params.set(ML_URL_PARAM_STATUS, st);
  }

  const sb = filter.sortBy ? String(filter.sortBy).toLowerCase() : "";
  if (sb && sb !== String(defaultSortBy).toLowerCase()) {
    params.set(ML_URL_PARAM_SORT, sb);
  }
};

/**
 * @param {URLSearchParams} params
 */
export const replaceHashPreservingPath = (params) => {
  if (typeof window === "undefined") {
    return;
  }
  const { path } = parseHashPathAndQuery();
  const qs = params.toString();
  const newHash = qs ? `${path}?${qs}` : path;
  const newUrl = `${window.location.pathname}${window.location.search}${newHash}`;
  if (
    `${window.location.pathname}${window.location.search}${window.location.hash}` !==
    newUrl
  ) {
    window.history.replaceState(null, "", newUrl);
  }
};

/**
 * @param {string[]} validStatus
 * @param {string[]} validSort
 * @returns {{
 *   hasMyLearningParams: boolean,
 *   categoryId?: number,
 *   selectedTags?: number[],
 *   searchText?: string,
 *   status?: string,
 *   sortBy?: string,
 * }}
 */
export const parseMyLearningFilterParamsFromHash = (validStatus, validSort) => {
  const { params } = parseHashPathAndQuery();
  const out = { hasMyLearningParams: false };

  const cat = params.get(ML_URL_PARAM_CAT);
  if (cat) {
    const n = parseInt(cat, 10);
    if (!Number.isNaN(n) && n > 0) {
      out.categoryId = n;
      out.hasMyLearningParams = true;
    }
  }

  const tagStr = params.get(ML_URL_PARAM_TAGS);
  if (tagStr) {
    out.selectedTags = tagStr.split(",").map((s) => {
      const n = parseInt(String(s).trim(), 10);
      return Number.isNaN(n) ? 0 : n;
    });
    out.hasMyLearningParams = true;
  }

  const q = params.get(ML_URL_PARAM_SEARCH);
  if (q) {
    out.searchText = q;
    out.hasMyLearningParams = true;
  }

  const st = params.get(ML_URL_PARAM_STATUS);
  if (st) {
    const lower = st.toLowerCase();
    if (validStatus.some((v) => String(v).toLowerCase() === lower)) {
      out.status = validStatus.find((v) => String(v).toLowerCase() === lower);
      out.hasMyLearningParams = true;
    }
  }

  const sort = params.get(ML_URL_PARAM_SORT);
  if (sort) {
    const lower = sort.toLowerCase();
    if (validSort.some((v) => String(v).toLowerCase() === lower)) {
      out.sortBy = validSort.find((v) => String(v).toLowerCase() === lower);
      out.hasMyLearningParams = true;
    }
  }

  return out;
};

/**
 * @param {number[]|undefined} parsedTags
 * @param {number} length
 * @returns {number[]}
 */
export const padSelectedTags = (parsedTags, length) => {
  const base = Array.isArray(parsedTags) ? [...parsedTags] : [];
  while (base.length < length) {
    base.push(0);
  }
  return base.slice(0, length);
};
